import React, { useEffect, memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, useMediaQuery } from "../../../Styles";
import { NewSelect, NewInput, NewInputList } from "../..";
import { NewFieldStyles, SelectFieldOption_Values, Pen, Section, ListStyle_Types } from "../../../Definitions";
import { useAppSelector } from "../../../Store";
import { Graph_QL, useDeviceInfo, TerminalTypesInfo } from "../../../Services";
import { useModalContext } from "../../LocationFeeder/Modals/_Modals/newSectionModalContext";
import { Station } from "../../../Definitions/newSectionModalInterfaces";

const NewEsfWfSectionModalLvl2: React.FC = () => {
  const { t } = useTranslation();
  const mobileXs = useMediaQuery(`(max-width: 805px)`);
  const { state: { farm, domain, connection, error, section }, dispatch } = useModalContext()
  const { data: deviceInfoState, call: getDeviceInfo } = useDeviceInfo(connection);
  const terminalTypesState = useAppSelector(({graphQlData}) => (graphQlData.domain.terminalTypes.entities))
  const terminalTypes = useMemo(() => { return terminalTypesState[domain]?.data ?? [] },[domain, terminalTypesState])
  const {__typename: inputTypename , input: inputBoolean, ...defaultInputTerminalConfig} = terminalTypes.find((terminalType) => (terminalType.terminal_type_id === 4))!;
  const {__typename, input, ...defaultOutputTerminalConfig} = terminalTypes.find((terminalTypes) => (terminalTypes.terminal_type_id === 50))!;

  const stations = { ...section, contents: section.contents as Station[] };
  
  const deviceInfo = useMemo(() => {
    if (deviceInfoState[domain]) {
      return deviceInfoState[domain].data.map((deviceInfo) => ({id: deviceInfo.device_id, name: deviceInfo.name, systemId: deviceInfo.system_assignment, type: 1}))
    }
    return []
  },[deviceInfoState, domain])

  useEffect(() => {
    getDeviceInfo({fetchAmount: 500}, connection);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Total number of stations
  const stationsWithFeederInput = (Section_stations: Station[]) => Section_stations.filter((s) => s.feederInput != null).length;
  const stationsWithFeederOutput = (Section_stations: Station[]) => Section_stations.filter((s) => s.feederOutput != null).length;

  const stationsWithGateInput = (Section_stations: Station[]) => Section_stations.filter((s) => s.gateMotorInput != null).length;
  const stationsWithGateOutput = (Section_stations: Station[]) => Section_stations.filter((s) => s.gateMotorOutput != null).length;
  const stationsWithGateSensor = (Section_stations: Station[]) => Section_stations.filter((s) => s.gateSensor != null).length;

  const stationsWithWaterOutput = (Section_stations: Station[]) => Section_stations.filter((s) => s.waterOutput != null).length;
  const stationsWithButtonInput = (Section_stations: Station[]) => Section_stations.filter((s) => s.buttonInput != null).length;
  
  const activeStationsCount = stations.contents.length;

  const totalOutputs = section.water ? (activeStationsCount * 3) : (activeStationsCount * 2)
  const totalInputs = section.button ? (activeStationsCount * 4) : (activeStationsCount * 3)
  const mainOutput = stationsWithFeederOutput(stations.contents) + (stationsWithGateOutput(stations.contents))
  const mainInput = (stationsWithFeederInput(stations.contents)) + (stationsWithGateInput(stations.contents)) + (stationsWithGateSensor(stations.contents))

  const remainingOutputs =
    section.water ? (totalOutputs - (mainOutput + (stationsWithWaterOutput(stations.contents))))
    : (totalOutputs - mainOutput)
      
  const remainingInputs = 
    section.button ? (totalInputs - (mainInput + (stationsWithButtonInput(stations.contents))))
    : (totalInputs - mainInput)

  const handleValueChange = (section: Section, stationIndex: number, newValue: string, counterKey: keyof Station, terminalConfig: Omit<TerminalTypesInfo, "input">) => {
    const updatedStations = stations.contents.map((station, index) => {
      if (index === stationIndex) {
        return {
          ...station,
          [counterKey]: newValue ? Number(newValue) : null,
          [counterKey+"Config"]: terminalConfig 
        };
      }
      return station;
    });
    dispatch({section:{contents:updatedStations}})
  };

  // SelectField Options
  const ioOptions = deviceInfo.map((type) => ({
    label: type.name, 
    id: type.id
  }));

  // Handler for select changes in IO
  const handleStationIOChange = (stationId: string, value?: SelectFieldOption_Values) => {
    const updatedStations = stations.contents.map((station) =>
      station.stationId === stationId ? { ...station, io: value?.id ? Number(value.id) : null } : station
    );
    dispatch({section:{contents: updatedStations}})
  };

  const placeholdersContent = () => {
    let content = [];

    if (section.button) content.push("");
    if (section.water) content.push("");
    
    return content;
  }

  const headersContent = () => {
    let content = [];

    if (section.water) content.push(t("locationFeederPages.sections.waterOutput", { defaultValue: "Water Output" }));
    if (section.button) content.push(t("locationFeederPages.sections.buttonInput", { defaultValue: "Button Input" }));
    
    return content;
  }

  const defaultValues = (index: number) => {
    let content = [];

    if (section.water) content.push(stations.contents[index].waterOutput ?? 0);
    if (section.button) content.push(stations.contents[index].buttonInput ?? 0);
    
    return content;
  }

  // #region | IO Fields
  // TODO selection of terminal types
  const ioFields = stations.contents.filter((station) => station.stationId !== null).map((station, index) => {
    const currentPlaceholders = placeholdersContent();
    const currentHeaders = headersContent();
    const currentDefaultValues = defaultValues(index);
    const ioFields = ( <>
      <NewInputList
        placeholders={["", ""]}
        headers={[
          t("locationFeederPages.sections.feederOutput", { defaultValue: "Feeder Output" }),
          t("locationFeederPages.sections.feederInput", { defaultValue: "Feeder Input" }),
        ]}
        onChanges={[
          (newValue) => handleValueChange(section, index, newValue, "feederOutput", defaultOutputTerminalConfig),
          (newValue) => handleValueChange(section, index, newValue, "feederInput", defaultInputTerminalConfig)
        ]}
        fieldOverrides={{
          fieldStyle: NewFieldStyles.greyTertiary,
          fieldWidth: mobileXs ? "100%" : "180px" 
        }}
        valueOverrides={{
          defaultValues: [stations.contents[index].feederOutput ?? 0, stations.contents[index].feederInput ?? 0],
          valueTypes: ["number", "number"],
          minValueNumbers: [1, 1]
        }}
        listOverrides={{
          listStyle: ListStyle_Types.horizontal,
          listGap: "9px"
        }}
      />
      <NewInputList
        placeholders={["", "", ""]}
        headers={[
          t("locationFeederPages.sections.gateMotorOutput", { defaultValue: "Gate Motor Output" }),
          t("locationFeederPages.sections.gateMotorInput", { defaultValue: "Gate Motor Input" }),
          t("locationFeederPages.sections.gateSensor", { defaultValue: "Gate Sensor" })
        ]}
        onChanges={[
          (newValue) => handleValueChange(section, index, newValue, "gateMotorOutput", defaultOutputTerminalConfig),
          (newValue) => handleValueChange(section, index, newValue, "gateMotorInput", defaultInputTerminalConfig),
          (newValue) => handleValueChange(section, index, newValue, "gateSensor", defaultInputTerminalConfig)
        ]}
        fieldOverrides={{
          fieldStyle: NewFieldStyles.greyTertiary,
          fieldWidth: mobileXs ? "100%" : "180px" 
        }}
        valueOverrides={{
          defaultValues: [ stations.contents[index].gateMotorOutput ?? 0, stations.contents[index].gateMotorInput ?? 0, stations.contents[index].gateSensor ?? 0 ],
          valueTypes: ["number", "number", "number"],
          minValueNumbers: [1, 1, 1]
        }}
        listOverrides={{
          listStyle: ListStyle_Types.horizontal,
          listGap: "9px"
        }}
      />
      <NewInputList
        placeholders={currentPlaceholders}
        headers={currentHeaders}
        onChanges={[
          (newValue) => handleValueChange(section, index, newValue, "waterOutput", defaultOutputTerminalConfig),
          (newValue) => handleValueChange(section, index, newValue, "buttonInput", defaultInputTerminalConfig)
        ]}
        fieldOverrides={{
          fieldStyle: NewFieldStyles.greyTertiary,
          fieldWidth: mobileXs ? "100%" : "180px" 
        }}
        valueOverrides={{
          defaultValues: currentDefaultValues,
          valueTypes: ["number", "number"],
          minValueNumbers: [1, 1]
        }}
        listOverrides={{
          listStyle: ListStyle_Types.horizontal,
          listGap: "9px"
        }}
      />
    </>);
    
    return (
      <div key={station.stationId} style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
        <NewSelect
          key={"select_"+station.stationId}
          header={`${t("locationFeederPages.locationFeeder.pen", {defaultValue: "Pen"})} ${station.name}`}
          placeholder={t("devices.chooseIo", {defaultValue: "Choose I/O"})}
          options={ioOptions}
          onChange={(selectedOption) => { if (station.stationId) { handleStationIOChange(station.stationId, selectedOption) } }}
          valueOverrides={{
            defaultValue: ioOptions.find(option => option.id === station.io)
          }}
        />
        <div style={{
          width: "calc(100% - 20px)",
          height: "fit-content",
          backgroundColor: colors.greyLightSecondary,
          padding: "10px 10px",
          borderRadius: "10px", display: "flex", flexDirection: "column", gap: "10px"}}>
          {ioFields}  
        </div>
      </div>
    );
  })
  // #endregion

  return (
    <div style={{ width: "100%", borderTop: "2px solid " + colors.blueDarkSecondary, margin: "20px 0px" }}>
      <div style={{ padding: "10px 10px", maxHeight: "40vh", overflowY: "auto", overflowX: "hidden", scrollbarWidth: "thin", scrollbarColor: colors.blueDarkSecondary + colors.transparent, display: "flex", alignItems: "center", justifyContent: "start", flexDirection: "column", gap: "15px" }}>
        {ioFields}  
      </div>
      <div style={{borderTop: "2px solid "+colors.blueDarkSecondary, borderBottom: "2px solid "+colors.blueDarkSecondary, padding: "10px", maxHeight: "200px", display: "flex", flexDirection: mobileXs ? "column" : "row", gap: mobileXs ? "10px" : undefined, alignItems: "center", justifyContent: "space-around" }}>
        <div style={{display: "flex", alignItems: "center", justifyContent: "center", gap: "10px"}}>         
          <p style={{...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary}}>{`${t("devices.remainingOutputs", {defaultValue:"Remaining Outputs"})}:`}</p>
          <p style={{...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary, backgroundColor: remainingOutputs === 0 ? colors.systemGreen : colors.systemRed, padding: "4px 5px", borderRadius: "6px"}}>{remainingOutputs}</p>
        </div>
        <div style={{display: "flex", alignItems: "center", justifyContent: "center", gap: "10px"}}>         
          <p style={{...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary}}>{`${t("devices.remainingInputs", {defaultValue:"Remaining Inputs"})}:`}</p>
          <p style={{...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary, backgroundColor: remainingInputs === 0 ? colors.systemGreen : colors.systemRed, padding: "4px 5px", borderRadius: "6px"}}>{remainingInputs}</p>
        </div>
      </div>
    </div>
  );
};

export default NewEsfWfSectionModalLvl2;
